// features/workstations/dashboard-sections/theses.js
// ═══════════════════════════════════════════════════════════
// THESES — Engine 2 (Thesis Registry) dashboard widget
// ═══════════════════════════════════════════════════════════
// Shows the user's active long-term theses and lets them:
//   • State a new thesis (compose form)
//   • Transition a thesis (pause / retire / accept inferred)
//   • Infer candidates from current state (open trades, rules,
//     repeated setups)
//
// Distinct from the DI Briefing's "daily thesis gate" — that's
// a per-day market read. THIS widget is for the longer-running
// strategic premises that Engines 3 (tensions) and 4 (options)
// and 6 (possibilities) all link against.
//
// Composed from primitives. The widget body reuses the same
// hybrid design language as the Briefing widget so they look
// like siblings on the dashboard.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
  MetaField,
  Toolbar,
  ToolbarButton,
  ToolbarMeta,
} from '../../../components/widgets/primitives';

// State badge colors — mirrors the briefing widget's STATE_TONES
// but we surface the values inline here too so this file is
// self-contained.
const STATE_TONES = {
  active:        'gold',
  strengthening: 'sage',
  holding:       'steel',
  weakening:     'amber',
  contradicted:  'terra',
  paused:        'steel',
  retired:       'steel',
};

const STATE_LABELS = {
  active:        'active',
  strengthening: 'strengthening',
  holding:       'holding',
  weakening:     'weakening',
  contradicted:  'contradicted',
  paused:        'paused',
  retired:       'retired',
};

// Categories come from the domain shape — but we don't import
// the shape here to keep this widget purely UI. Instead we
// declare the trading categories inline and fall back to a
// free-text field for other domains. (When Medicine/Law ship,
// their categories arrive here via a small config lookup.)
const CATEGORIES_BY_DOMAIN = {
  trading: [
    { key: 'market_bias',          label: 'Market bias',          hint: 'e.g. SPY closes above 500 by end of week' },
    { key: 'setup_edge',            label: 'Setup edge',           hint: 'e.g. 9:45 reversal works on gap-down days' },
    { key: 'regime',                label: 'Regime view',          hint: 'e.g. mean-reverting regime — momentum fails' },
    { key: 'rule',                  label: 'Hard rule',            hint: 'e.g. no trades in the first 15 minutes' },
    { key: 'instrument_specific',   label: 'Instrument-specific',  hint: 'e.g. NVDA rangebound 120–140 into earnings' },
  ],
};

// ═══════════════════════════════════════════════════════════
// TOP-LEVEL SECTION
// ═══════════════════════════════════════════════════════════
export default function ThesesSection({ workstation, userId }) {
  const [theses, setTheses] = useState([]);
  const [proposed, setProposed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [error, setError] = useState(null);

  const domain = workstation?.domain_key || 'trading';

  // ─── Load active theses ───
  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        user_id: userId,
        domain,
        workstation_id: workstation.id,
      });
      const res = await fetch(`/api/engines/theses?${qs}`, { cache: 'no-store' });
      const data = res.ok ? await res.json() : { theses: [] };
      const all = Array.isArray(data.theses) ? data.theses : [];
      // Split out proposed/inferred (not yet user_accepted) from active
      setTheses(all.filter(t => t.source === 'stated' || t.user_accepted_at));
      setProposed(all.filter(t => !t.user_accepted_at && t.source !== 'stated'));
    } catch (e) {
      console.warn('[theses] load failed:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, domain]);

  useEffect(() => { load(); }, [load]);

  // ─── Create a thesis ───
  const create = useCallback(async (payload) => {
    setError(null);
    try {
      const res = await fetch('/api/engines/theses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          domain,
          workstation_id: workstation?.id || null,
          ...payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setComposing(false);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Create failed');
      return false;
    }
  }, [userId, domain, workstation?.id, load]);

  // ─── Transition a thesis (pause / retire / accept) ───
  const transition = useCallback(async (thesisId, action) => {
    try {
      const body = action === 'accept'
        ? { user_id: userId, action: 'accept' }
        : { user_id: userId, action: 'transition', to_state: action };
      const res = await fetch(`/api/engines/theses/${thesisId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || `HTTP ${res.status}`);
        return;
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Transition failed');
    }
  }, [userId, load]);

  // ─── Infer candidates (proposed list) ───
  const runInfer = useCallback(async () => {
    if (!workstation?.id || !userId || inferring) return;
    setInferring(true);
    setError(null);
    try {
      const res = await fetch('/api/engines/theses/infer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          domain,
          workstation_id: workstation.id,
          auto_insert: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return;
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Infer failed');
    } finally {
      setInferring(false);
    }
  }, [userId, domain, workstation?.id, inferring, load]);

  // ─── Render ───
  const count = theses.length;
  const categories = CATEGORIES_BY_DOMAIN[domain] || null;

  return (
    <section className="ws-theses">
      {loading && <div className="ws-theses-loading">Loading theses…</div>}

      {!loading && (
        <div className="ws-theses-body">
          <Eyebrow parts={[
            'theses',
            count === 0 ? 'none stated' : `${count} active`,
          ]} />

          {count === 0 ? (
            <>
              <SerifHeadline>Nothing stated yet.</SerifHeadline>
              <SerifSummary>
                A thesis is a strategic premise you&rsquo;re holding — a market bias,
                a setup edge, a hard rule, a regime view. The engines watch these:
                tensions flag when evidence drifts against them, options link back
                to them, possibilities cite them as analogs.
              </SerifSummary>
            </>
          ) : (
            <SerifSummary>
              Active theses the engines watch. Pause one to stop monitoring
              without losing history; retire one when it no longer applies.
            </SerifSummary>
          )}

          <Toolbar>
            <ToolbarButton onClick={() => setComposing(v => !v)}>
              {composing ? '✕ cancel' : '+ state a thesis'}
            </ToolbarButton>
            <ToolbarButton onClick={runInfer} disabled={inferring} title="Scan open positions, active rules, repeated setups for candidate theses">
              {inferring ? 'Inferring…' : 'Infer from state'}
            </ToolbarButton>
            <ToolbarMeta>{error ? error : ''}</ToolbarMeta>
          </Toolbar>

          {composing && (
            <ComposeThesis
              categories={categories}
              onSave={create}
              onCancel={() => setComposing(false)}
            />
          )}

          {/* Active theses as pill rows */}
          {count > 0 && (
            <SectionStrip label="active" count={count}>
              <PillTable>
                {theses.map((t) => (
                  <ThesisPill
                    key={t.id}
                    thesis={t}
                    onTransition={transition}
                  />
                ))}
              </PillTable>
            </SectionStrip>
          )}

          {/* Proposed / inferred — awaiting user acceptance */}
          {proposed.length > 0 && (
            <SectionStrip label="proposed — confirm or dismiss" count={proposed.length}>
              <div className="ws-theses-proposed-list">
                {proposed.map((t) => (
                  <ProposedThesisRow
                    key={t.id}
                    thesis={t}
                    onAccept={() => transition(t.id, 'accept')}
                    onDismiss={() => transition(t.id, 'retired')}
                  />
                ))}
              </div>
            </SectionStrip>
          )}
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// THESIS PILL — one row in the active list
// ═══════════════════════════════════════════════════════════
function ThesisPill({ thesis, onTransition }) {
  const [expanded, setExpanded] = useState(false);
  const tone = STATE_TONES[thesis.state] || 'steel';
  const stateLabel = STATE_LABELS[thesis.state] || thesis.state;
  const confPct = typeof thesis.confidence === 'number'
    ? `${Math.round(thesis.confidence * 100)}%`
    : null;
  const evidenceFor = Array.isArray(thesis.evidence_for) ? thesis.evidence_for : [];
  const evidenceAgainst = Array.isArray(thesis.evidence_against) ? thesis.evidence_against : [];

  return (
    <div className="ws-theses-row">
      <button
        type="button"
        className="ws-theses-pill-trigger"
        onClick={() => setExpanded(v => !v)}
        title={expanded ? 'Collapse' : 'Show evidence + actions'}
      >
        <PillRow
          keyText={thesis.premise_short || thesis.premise || '(unnamed)'}
          valueText={confPct ? `${stateLabel} · ${confPct}` : stateLabel}
          tone={tone}
        />
      </button>

      {expanded && (
        <div className="ws-theses-pill-drawer">
          <div className="ws-theses-premise">{thesis.premise}</div>

          {(evidenceFor.length > 0 || evidenceAgainst.length > 0) && (
            <div className="ws-theses-evidence-grid">
              <div className="ws-theses-evidence-col">
                <div className="ws-theses-evidence-head">
                  for ({evidenceFor.length})
                </div>
                {evidenceFor.length === 0 ? (
                  <div className="ws-theses-evidence-empty">—</div>
                ) : (
                  <ul className="ws-theses-evidence-list">
                    {evidenceFor.slice(0, 5).map((e, i) => (
                      <li key={i}>
                        <span className="ws-theses-evidence-src">{e.source || 'src'}</span>
                        {' '}
                        <span>{truncate(e.content, 120)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="ws-theses-evidence-col">
                <div className="ws-theses-evidence-head">
                  against ({evidenceAgainst.length})
                </div>
                {evidenceAgainst.length === 0 ? (
                  <div className="ws-theses-evidence-empty">—</div>
                ) : (
                  <ul className="ws-theses-evidence-list">
                    {evidenceAgainst.slice(0, 5).map((e, i) => (
                      <li key={i}>
                        <span className="ws-theses-evidence-src">{e.source || 'src'}</span>
                        {' '}
                        <span>{truncate(e.content, 120)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {thesis.inferred_from?.category && (
            <MetaField label="category">
              {thesis.inferred_from.category}
            </MetaField>
          )}
          {thesis.expires_at && (
            <MetaField label="horizon">
              through {formatDate(thesis.expires_at)}
            </MetaField>
          )}
          {thesis.state === 'contradicted' && thesis.contradicted_by && (
            <PractitionerLens label="contradicted by">
              {typeof thesis.contradicted_by === 'string'
                ? thesis.contradicted_by
                : JSON.stringify(thesis.contradicted_by)}
            </PractitionerLens>
          )}

          <div className="ws-theses-actions">
            {thesis.state !== 'paused' && (
              <button type="button" className="ws-theses-action ws-theses-action-steel" onClick={() => onTransition(thesis.id, 'paused')}>
                pause
              </button>
            )}
            {thesis.state === 'paused' && (
              <button type="button" className="ws-theses-action ws-theses-action-sage" onClick={() => onTransition(thesis.id, 'active')}>
                resume
              </button>
            )}
            <button type="button" className="ws-theses-action ws-theses-action-terra" onClick={() => onTransition(thesis.id, 'retired')}>
              retire
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PROPOSED THESIS — inferred row awaiting user acceptance
// ═══════════════════════════════════════════════════════════
function ProposedThesisRow({ thesis, onAccept, onDismiss }) {
  const sourceLabel = thesis.inferred_from?.source || 'inferred';
  return (
    <div className="ws-theses-proposed">
      <div className="ws-theses-proposed-head">
        <span className="ws-theses-proposed-src">{sourceLabel}</span>
        <span className="ws-theses-proposed-what">
          {thesis.premise_short || thesis.premise}
        </span>
      </div>
      <div className="ws-theses-actions">
        <button type="button" className="ws-theses-action ws-theses-action-sage" onClick={onAccept}>
          accept
        </button>
        <button type="button" className="ws-theses-action ws-theses-action-terra" onClick={onDismiss}>
          dismiss
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPOSE FORM — inline drawer for stating a new thesis
// ═══════════════════════════════════════════════════════════
function ComposeThesis({ categories, onSave, onCancel }) {
  const [premise, setPremise] = useState('');
  const [category, setCategory] = useState(categories?.[0]?.key || '');
  const [confidence, setConfidence] = useState('');
  const [horizon, setHorizon] = useState('');
  const [saving, setSaving] = useState(false);

  const chosen = useMemo(
    () => (categories || []).find(c => c.key === category) || null,
    [categories, category],
  );

  const disabled = !premise.trim() || saving;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setSaving(true);
    const payload = { premise: premise.trim() };
    if (category) payload.category = category;
    if (confidence) {
      const n = Number(confidence);
      if (!Number.isNaN(n) && n >= 0 && n <= 1) payload.confidence = n;
    }
    if (horizon) {
      const days = Number(horizon);
      if (!Number.isNaN(days) && days > 0) {
        payload.expires_at = new Date(Date.now() + days * 86400000).toISOString();
      }
    }
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) {
      setPremise('');
      setCategory(categories?.[0]?.key || '');
      setConfidence('');
      setHorizon('');
    }
  };

  return (
    <form className="ws-theses-compose" onSubmit={onSubmit}>
      <div className="ws-theses-compose-row">
        <label className="ws-theses-label">premise</label>
        <textarea
          className="ws-theses-textarea"
          placeholder={chosen?.hint || 'State the premise you&rsquo;re holding.'}
          value={premise}
          onChange={(e) => setPremise(e.target.value)}
          rows={2}
          autoFocus
        />
      </div>

      {categories && (
        <div className="ws-theses-compose-row ws-theses-compose-inline">
          <label className="ws-theses-label">category</label>
          <select
            className="ws-theses-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categories.map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="ws-theses-compose-row ws-theses-compose-inline">
        <label className="ws-theses-label">confidence</label>
        <input
          className="ws-theses-input"
          type="number"
          step="0.05"
          min="0"
          max="1"
          placeholder="0.6 — optional"
          value={confidence}
          onChange={(e) => setConfidence(e.target.value)}
        />
        <label className="ws-theses-label ws-theses-label-sep">horizon (days)</label>
        <input
          className="ws-theses-input"
          type="number"
          min="1"
          step="1"
          placeholder="optional"
          value={horizon}
          onChange={(e) => setHorizon(e.target.value)}
        />
      </div>

      <div className="ws-theses-compose-actions">
        <button type="submit" disabled={disabled} className="ws-theses-action ws-theses-action-sage">
          {saving ? 'Saving…' : 'state thesis'}
        </button>
        <button type="button" onClick={onCancel} className="ws-theses-action ws-theses-action-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

// ─── Helpers ───
function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
function formatDate(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}
